<template>
  <div>
    <v-row v-if="stats" dense>
      <v-col cols="12" class="pl-3 pb-0 mt-3">
        <v-card
          class="ir-stats-card d-flex flex-wrap align-center"
          elevation="0"
        >
          <div class="ir-stats-title font-weight-medium">
            {{ $t("incidentReports.statistics.title") }}:
            <b>{{ stats.total }}</b>
          </div>

          <v-chip small class="ir-stats-chip" color="primary" outlined>
            {{ $t("incidentReports.statistics.thisYear") }}
            <span class="ml-1">
              <b>{{ stats.thisYear }}</b>
            </span>
          </v-chip>

          <v-chip small class="ir-stats-chip" color="primary" outlined>
            {{ $t("incidentReports.statistics.thisMonth") }}
            <span class="ml-1">
              <b>{{ stats.thisMonth }}</b>
            </span>
          </v-chip>

          <v-chip small class="ir-stats-chip" outlined>
            {{ $t("incidentReports.statistics.reportable") }}
            <span class="ml-1">({{ stats.byType.long }})</span>
          </v-chip>

          <v-chip small class="ir-stats-chip" outlined>
            {{ $t("incidentReports.statistics.nonReportable") }}
            <span class="ml-1">({{ stats.byType.short }})</span>
          </v-chip>

          <v-chip small class="ir-stats-chip" outlined>
            {{ $t("incidentReports.statistics.quick") }}
            <span class="ml-1">({{ stats.byType.quick }})</span>
          </v-chip>
        </v-card>
      </v-col>
    </v-row>

    <tableComponent
      ref="tableComponent"
      :tableSettings="tableSettings"
      :tableData="tableData"
      :headers="headers"
      @newDocument="openMenu"
      @exportWithTimespan="exportWithTimespan"
      @openIncidentReportQrCodeDialog="openIncidentReportQrCodeDialog"
      @openQrCodePoster="openQrCodePoster"
      @openMultiUserDialog="openMultiUserDialog"
      @confirmBulkDelete="confirmBulkDelete"
    />
    <v-dialog v-model="bulkConfirm.open" max-width="440">
      <v-card>
        <v-card-title>{{ bulkConfirm.title }}</v-card-title>
        <v-card-text>{{ bulkConfirm.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="bulkConfirm.open = false"
            :disabled="bulkConfirm.loading"
          >
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            :loading="bulkConfirm.loading"
            @click="runBulkDelete"
          >
            {{ $t("incidentReports.bulk.deleteButton") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="isMenuOpen" max-width="800px">
      <v-card>
        <v-list>
          <v-list-item>
            <v-btn block color="primary" @click="newDocumentShort">
              {{ $t("incidentReports.chooseType.nonReportable") }}
              {{ $t("incidentReports.chooseType.nonReportableSub") }}
            </v-btn>
          </v-list-item>
          <v-list-item>
            <v-btn block color="primary" @click="newDocumentLong">
              {{ $t("incidentReports.chooseType.reportable") }}
              {{ $t("incidentReports.chooseType.reportableSub") }}
            </v-btn>
          </v-list-item>
          <v-list-item>
            <v-btn block color="primary" @click="newDocumentQuick">
              {{ $t("incidentReports.chooseType.nearMiss") }}
            </v-btn>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>
    <incidentReportExportForm
      ref="incidentReportExportForm"
      :data="incidentReportExportForm"
      @reloadData="reloadDataFunc"
    />
    <incidentReportQrCodeDialog
      :incidentReportQrCodeDialog="incidentReportQrCodeDialog"
      @printPoster="openQrCodePoster"
    />
    <multiUserDialog
      v-model="multiUserOpen"
      entityType="incidentReport"
      :availableItems="transferItems"
      itemText="_displayName"
      itemValue="_id"
      :clients="transferClients"
      :currentClientId="currentClientId"
      @confirm="executeTransfer"
      entityLabel="multiUser.dialog.incidentReports"
    />
  </div>
</template>
<script>
import i18n from "../../i18n";
import { getUniqueId } from "../../components/api/api";
import { downloadQrPoster } from "../../utils/posterDownload";
import tableComponent from "../../components/_utils/dataTableHelpers/tableComponent.vue";
import incidentReportExportForm from "./incidentReportExportForm.vue";
import incidentReportQrCodeDialog from "./incidentReportQrCodeDialog.vue";
import multiUserDialog from "../../components/_utils/dataTableHelpers/actions/multiUserDialog.vue";

export default {
  components: {
    incidentReportQrCodeDialog,
    tableComponent,
    incidentReportExportForm,
    multiUserDialog,
  },
  data: () => ({
    currentUserId: "",
    incidentReportQrCodeDialog: {
      open: false,
    },
    isMenuOpen: false,
    bulkConfirm: {
      open: false,
      loading: false,
      title: "",
      message: "",
      ids: [],
    },
    tableSettings: {
      title: "",
      showSelect: true,
      showHeaderToolbar: true,
      multipleSelectButtons: [
        {
          title: "incidentReports.bulk.deleteButton",
          color: "error",
          action: "confirmBulkDelete",
          icon: "delete",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
      headerButtons: [
        {
          title: "multiUser.button",
          color: "primary",
          action: "openMultiUserDialog",
          icon: "mdi-swap-horizontal",
          iconDark: true,
          dark: true,
        },
        {
          title: "medkitlogs.generateQrCode",
          color: "primary",
          action: "openIncidentReportQrCodeDialog",
          icon: "qr_code",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "incidentReports.createExport",
          color: "grey",
          action: "exportWithTimespan",
          icon: "download",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "incidentReports.createIncidentReports",
          color: "secondary",
          action: "newDocument",
          icon: "add",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
    },
    tableData: {
      tableComponentSlots: [
        {
          id: getUniqueId(),
          slotName: "item.formattedIncidentReportName",
          componentName: "formattedIncidentReportName",
        },
        {
          id: getUniqueId(),
          slotName: "item.formattedIncidentReportDate",
          componentName: "formattedIncidentReportDate",
        },
        {
          id: getUniqueId(),
          slotName: "item.formattedIncidentReportBackToWork",
          componentName: "formattedIncidentReportBackToWork",
        },
        {
          id: getUniqueId(),
          slotName: "item.formattedIncidentReportCommutingIncident",
          componentName: "formattedIncidentReportCommutingIncident",
        },
        {
          id: getUniqueId(),
          slotName: "item.incidentReportFormattedUnableToWorkDays",
          componentName: "incidentReportFormattedUnableToWorkDays",
        },
        {
          id: getUniqueId(),
          slotName: "item.incidentReportFormattedCreatedAt",
          componentName: "incidentReportFormattedCreatedAt",
        },
        {
          id: getUniqueId(),
          slotName: "item.incidentReportProofStatus",
          componentName: "incidentReportProofStatus",
        },
        {
          id: getUniqueId(),
          slotName: "item.incidentReportAction",
          componentName: "incidentReportAction",
          config: { currentUserId: "" },
        },
      ],
      itemKey: "_id",
      uriPartPrepend: "incidentReport",
      uriPartAppend: "",
      populate: [],
      sort: "-createdAt",
      editFormPushName: "incidentReportForm",
    },
    incidentReportExportForm: {
      open: false,
      title: i18n.t("incidentReports.exportTitle"),
      item: null,
      method: "export",
    },
    currentClientId: "",
    isMultiUser: false,
    multiUserOpen: false,
    transferClients: [],
    transferItems: [],
    stats: null,
  }),
  async created() {
    this.$store.dispatch("getUserInfo").then((user) => {
      this.currentUserId = user?._id || "";
      const slot = this.tableData.tableComponentSlots.find(
        (x) => x.componentName === "incidentReportAction"
      );
      if (slot) slot.config = { currentUserId: this.currentUserId };
    });
    const { year, month } = this.$route.params;
    if (year && month) {
      this.tableData.uriPartAppend = `&year=${year}&month=${month}`;
    }

    const user = await this.$store.dispatch("getUserInfo");
    this.currentClientId = user?.client?._id || "";
    this.isMultiUser = user?.role === "MultiUser";

    this.fetchStats();

    const res = await this.$store.dispatch("getAllIncidentReports");
    const raw = res.data || res;

    this.transferItems = raw.map((item) => ({
      ...item,
      _displayName:
        item.incidentReportType === "long"
          ? [
              item.longIncidentReport?.insuredPerson?.firstName,
              item.longIncidentReport?.insuredPerson?.lastName,
            ]
              .filter(Boolean)
              .join(" ") ||
            item.name ||
            "—"
          : item.name || "—",
    }));

    if (!this.isMultiUser) {
      this.tableSettings.headerButtons =
        this.tableSettings.headerButtons.filter(
          (btn) => btn.action !== "openMultiUserDialog"
        );
    }
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("incidentReports.tableHeader.name"),
          value: "formattedIncidentReportName",
          align: "left",
        },
        {
          text: this.$t("incidentReports.tableHeader.incidentDate"),
          value: "formattedIncidentReportDate",
        },
        {
          text: this.$t("incidentReports.tableHeader.backToWork"),
          value: "formattedIncidentReportBackToWork",
        },
        {
          text: this.$t("incidentReports.tableHeader.wayIncident"),
          value: "formattedIncidentReportCommutingIncident",
        },
        {
          text: this.$t("incidentReports.tableHeader.absenceInDays"),
          value: "incidentReportFormattedUnableToWorkDays",
        },
        {
          text: this.$t("incidentReports.tableHeader.createdAt"),
          value: "incidentReportFormattedCreatedAt",
        },
        {
          text: this.$t("riskAssessment.tableHeader.proofed"),
          value: "incidentReportProofStatus",
          sortable: false,
        },
        {
          text: "",
          value: "incidentReportAction",
          align: "right",
          sortable: false,
        },
      ];
    },
  },
  methods: {
    async openMultiUserDialog() {
      try {
        const res = await this.$store.dispatch("getTransferClients");
        this.transferClients = res.data || [];
      } catch (e) {
        return;
      }
      this.multiUserOpen = true;
    },

    async executeTransfer(payload) {
      try {
        const result = await this.$store.dispatch("transferEntities", payload);

        const parts = [];
        if (result.totalSuccess > 0) {
          parts.push(
            this.$t("multiUser.result.success", {
              number: result.totalSuccess,
            })
          );
        }
        if (result.totalConflict > 0) {
          parts.push(
            this.$t("multiUser.result.error", { number: result.totalConflict })
          );
        }
        if (result.totalError > 0) {
          parts.push(
            this.$t("multiUser.result.error", { number: result.totalError })
          );
        }

        this.$store.commit("CREATE_SNACKBAR", parts.join(" · "));

        if (payload.action === "move" && result.totalSuccess > 0) {
          this.reloadDataFunc();
        }
      } catch (e) {
        console.error("Transfer failed:", e);
      }
    },
    confirmBulkDelete(selectedItems) {
      const ids = (selectedItems || []).map((i) => i._id);
      this.bulkConfirm = {
        open: true,
        loading: false,
        title: this.$t("incidentReports.bulk.deleteTitle"),
        message: this.$t("incidentReports.bulk.deleteConfirm", {
          count: ids.length,
        }),
        ids,
      };
    },
    async runBulkDelete() {
      this.bulkConfirm.loading = true;
      try {
        await this.$store.dispatch(
          "bulkDeleteIncidentReports",
          this.bulkConfirm.ids
        );
        this.bulkConfirm.open = false;
        if (this.$refs.tableComponent) {
          this.$refs.tableComponent.clearSelectItems();
        }
        this.reloadDataFunc();
      } finally {
        this.bulkConfirm.loading = false;
      }
    },
    reloadDataFunc() {
      this.$refs.tableComponent.getData();
      this.fetchStats();
    },
    async fetchStats() {
      try {
        this.stats = await this.$store.dispatch("getIncidentReportStats");
      } catch (e) {
        this.stats = null;
      }
    },
    openMenu() {
      this.isMenuOpen = true;
    },
    newDocumentShort() {
      const { year, month } = this.$route.params;
      this.$router.push({
        name: this.tableData.editFormPushName,
        params: { id: "1", mode: "new", data: 1 },
        query: year && month ? { year, month } : {},
      });
    },
    newDocumentLong() {
      const { year, month } = this.$route.params;
      this.$router.push({
        name: "incidentReportFormLong",
        params: { id: "1", mode: "new", data: 1 },
        query: year && month ? { year, month } : {},
      });
    },
    newDocumentQuick() {
      const { year, month } = this.$route.params;
      this.$router.push({
        name: "incidentReportFormQuick",
        params: { id: "1", mode: "new", data: 1 },
        query: year && month ? { year, month } : {},
      });
    },
    exportWithTimespan() {
      this.incidentReportExportForm.open = true;
    },
    deleteItem(item) {
      //trigger multiple select delete
      this.$store.dispatch("deleteMultipleIncidentReport", item).then(() => {
        this.reloadDataFunc();
        this.multipleDeleteDialog.open = false;
        this.multipleDeleteDialog.items = [];
      });
    },

    openIncidentReportQrCodeDialog() {
      this.incidentReportQrCodeDialog.open = true;
    },
    async openQrCodePoster() {
      try {
        const settings = await this.$store.dispatch(
          "getSettingData",
          "qrCodeKeys"
        );
        let incidentKey = settings.value.incidentReport;
        let medkitKey = settings.value.medkitlog;

        if (!incidentKey) {
          const t = await this.$store.dispatch("newIncidentReportQrCode");
          incidentKey = t.token;
        }
        if (!medkitKey) {
          const t = await this.$store.dispatch("newMedkitlogQrCode");
          medkitKey = t.token;
        }

        await downloadQrPoster(incidentKey, medkitKey);
      } catch (e) {
        console.error(e);
      }
    },
  },
};
</script>

<style>
.ir-stats-card {
  padding: 4px 8px;
  gap: 6px;
  font-size: 0.875rem;
  line-height: 1.2;
}

.ir-stats-title {
  margin-right: 8px;
  white-space: nowrap;
}

.ir-stats-chip {
  height: 22px;
  font-size: 0.75rem;
}
</style>
